import { useEffect } from 'react';
import { KeyRecoilProps, TPickDateAction } from '@/shared/model';
import {
  selectControlModal,
  selectIsActiveTab,
  selectPickedEndDate,
  selectPickedStartDate,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';

/**
 * Keeps the ECG events tab's modal and date-picking state consistent as the reviewer changes strips, tabs and modals. Resets the control modal when the selected strip changes outside the add-event flows and when the tab goes inactive, advances the pick-date action from START through END to NONE as the picked dates fill in, and clears both picked dates whenever a modal other than the duration-plus-add-event combination is open.
 *
 * @param keyRecoil - Store record key identifying which report tab's UI state this effect drives.
 * @returns Nothing; this hook only runs the modal/date-picking effects.
 */
const useTabECGEventUIEffect = ({ keyRecoil }: KeyRecoilProps) => {
  const setPickDateActionAction = useReportStore((s) => s.actions.setPickDateAction);
  /**
   * Sets the pick-date action for the ECG events tab, which decides whether the viewer is waiting for a start date, an end date or nothing. Forwards the value to the report store's setPickDateAction action with keyRecoil already bound.
   *
   * @param v - The pick-date action to store.
   * @returns The result of the underlying store action call.
   */
  const setPickDateAction = (v: Parameters<typeof setPickDateActionAction>[1]) =>
    setPickDateActionAction(keyRecoil, v);
  const pickedStartDate = useReportStore(selectPickedStartDate(keyRecoil));
  const setPickedStartDateAction = useReportStore((s) => s.actions.setPickedStartDate);
  /**
   * Stores the start date the reviewer picked on the ECG events tab. Forwards the value to the report store's setPickedStartDate action with keyRecoil already bound.
   *
   * @param v - The picked start date, or null to clear it.
   * @returns The result of the underlying store action call.
   */
  const setPickedStartDate = (v: Parameters<typeof setPickedStartDateAction>[1]) =>
    setPickedStartDateAction(keyRecoil, v);
  const pickedEndDate = useReportStore(selectPickedEndDate(keyRecoil));
  const setPickedEndDateAction = useReportStore((s) => s.actions.setPickedEndDate);
  /**
   * Stores the end date the reviewer picked on the ECG events tab. Forwards the value to the report store's setPickedEndDate action with keyRecoil already bound.
   *
   * @param v - The picked end date, or null to clear it.
   * @returns The result of the underlying store action call.
   */
  const setPickedEndDate = (v: Parameters<typeof setPickedEndDateAction>[1]) =>
    setPickedEndDateAction(keyRecoil, v);
  const controlModal = useReportStore(selectControlModal(keyRecoil));
  const resetControlModalAction = useReportStore((s) => s.actions.resetControlModal);
  /**
   * Clears the ECG events tab's control modal flags. Calls the report store's resetControlModal action with the tab's keyRecoil already applied.
   *
   * @returns The result of the underlying store action call.
   */
  const resetControlModal = () => resetControlModalAction(keyRecoil);
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));

  useEffect(() => {
    if (!controlModal.isShowNewEvents && !controlModal.isShowAddNewEvent) {
      resetControlModal();
    }
  }, [selectedStrip]);

  useEffect(() => {
    if (!isActiveTab) {
      resetControlModal();
    }
  }, [isActiveTab]);

  //* Pick date handler
  useEffect(() => {
    if (pickedStartDate && !pickedEndDate) {
      setPickDateAction(TPickDateAction.END);
    }
    if (pickedStartDate && pickedEndDate) {
      setPickDateAction(TPickDateAction.NONE);
    }
    if (!pickedStartDate && pickedEndDate) {
      setPickDateAction(TPickDateAction.NONE);
    }
  }, [pickedStartDate, pickedEndDate]);

  useEffect(() => {
    if (Object.keys(controlModal).length > 1) {
      if (
        !(
          (controlModal.isShowChangeDurationButtonType ||
            controlModal.isShowChangeDurationSelectedInfo) &&
          controlModal.isShowAddNewEvent
        )
      ) {
        setPickedStartDate(null);
        setPickedEndDate(null);
        setPickDateAction(TPickDateAction.NONE);
      }
      if (
        controlModal.isShowChangeDurationButtonType ||
        controlModal.isShowChangeDurationSelectedInfo ||
        controlModal.isShowAddNewEvent
      ) {
        setPickDateAction(TPickDateAction.START);
      }
    }
  }, [controlModal]);
};

export default useTabECGEventUIEffect;
